import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, getYear } from "date-fns";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { CalculateInputTrigger } from "components/ui/CalculateInputTrigger/CalculateInputTrigger";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { InfoTooltip } from "components/ui/InfoTooltip";
import { MultiSelect } from "components/ui/MultiSelect/MultiSelect";
import { Select } from "components/ui/Select/Select";
import { AuditCCalculatorModal } from "features/alcoholRiskScore/AuditCCalculatorModal/AuditCCalculatorModal";
import { InterventionInfoHints } from "features/cdm/components/InterventionInfoHints/InterventionInfoHints";
import { ItemFormWrapper } from "features/cdm/components/ItemFormWrapper/ItemFormWrapper";
import {
  ALCOHOL_DEPENDENCE_INTERVENTIONS_OPTIONS,
  ALCOHOL_HIGHER_RISK_INTERVENTIONS_OPTIONS,
  ALCOHOL_INCREASING_RISK_INTERVENTIONS_OPTIONS,
  MECC_INTERVENTION_INFO,
  PHYSICAL_ACTIVITY_INTERVENTIONS_OPTIONS,
  PHYSICAL_ACTIVITY_Q1_OPTIONS,
  PHYSICAL_ACTIVITY_Q2_OPTIONS,
  SMOKING_INTERVENTIONS_OPTIONS,
  SMOKING_STATUS_OPTIONS,
  VAPING_STATUS_OPTIONS,
  WEIGHT_HIGH_RISK_INTERVENTIONS_OPTIONS,
  WEIGHT_INCREASED_RISK_INTERVENTIONS_OPTIONS,
  WEIGHT_NORMAL_INTERVENTIONS_OPTIONS,
  YES_NO_OPTIONS,
} from "features/cdm/constants/formOptions";
import { isPpReasonVisible } from "features/cdm/pp/utils/visibleRegistrationReasons";
import { getWeightMeccBand, WEIGHT_MECC_BAND } from "features/cdm/utils/weightMecc";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { usePatientHealthIdentifiers } from "features/patients/hooks/usePatientHealthIdentifiers";
import { QRisk3CalculatorModal } from "features/qriskScore/QRisk3CalculatorModal/QRisk3CalculatorModal";
import { getAlcoholInterventionCategory } from "utils/auditC";
import { onInvalidSubmit } from "utils/reportFormErrors";

import { additionalRiskFactorItems } from "../../../../constants/additionalRiskFactorItems";
import dialogStyle from "../../../AddPPReviewDialog/AddPPReviewDialog.module.css";
import style from "./RiskAndAssesmentForm.module.css";


const additionalRiskFactorSchema = additionalRiskFactorItems.reduce(
  (acc, item) => ({
    ...acc,
    [item.field]: z.boolean().optional().default(false),
    [`${item.field}Year`]: z.string().optional(),
  }),
  {}
);

const formSchema = (bmi) =>
  z.object({
    ...additionalRiskFactorSchema,
    // QRisk3 lives on this step (which now runs AFTER Tests & Investigations) so
    // the calculator can prefill from the bloods the clinician has just entered.
    qRisk3Score: z.string().min(1, "QRisk 3 score is required"),
    tcHdlRatio: z.string().optional(),
    smokingStatus: z.string().min(1, "Smoking status is required"),
    vapingStatus: z.string().min(1, "Vaping status is required"),
    alcoholRiskScore: z.string().min(1, "Alcohol risk score is required"),
    alcoholFullAuditScore: z.string().optional(),
    physicalActivityQ1: z.string().min(1, "Physical activity is required"),
    physicalActivityQ2: z.string().min(1, "Physical activity is required"),
    // Ref 17: required only when the patient is a current smoker.
    smokingInterventions: z.array(z.string()).optional(),
    alcoholDependenceInterventions: z.array(z.string()).optional(),
    alcoholHigherRiskInterventions: z.array(z.string()).optional(),
    alcoholIncreasingRiskInterventions: z.array(z.string()).optional(),
    // Ref 26/27/28: only the BMI-band-appropriate intervention is required.
    weightHighRiskInterventions: z.array(z.string()).optional(),
    weightIncreasedRiskInterventions: z.array(z.string()).optional(),
    weightNormalInterventions: z.array(z.string()).optional(),
    physicalActivityInterventions: z.array(z.string()).optional(),
    medicationReview: z.string().min(1, "Medication review is required"),
    yearOfRegistration: z.string().min(1, "Year of registration is required"),
  }).superRefine((data, ctx) => {
    if (data.smokingStatus === "CURRENT" && !data.smokingInterventions?.length) {
      ctx.addIssue({ code: "custom", path: ["smokingInterventions"], message: "Smoking interventions is required" });
    }

    const category = getAlcoholInterventionCategory(data.alcoholFullAuditScore);

    if (category === "DEPENDENCE" && !data.alcoholDependenceInterventions?.length) {
      ctx.addIssue({ code: "custom", path: ["alcoholDependenceInterventions"], message: "Alcohol dependence interventions is required" });
    }

    if (category === "HIGHER_RISK" && !data.alcoholHigherRiskInterventions?.length) {
      ctx.addIssue({ code: "custom", path: ["alcoholHigherRiskInterventions"], message: "Alcohol higher risk interventions is required" });
    }

    if (category === "INCREASING_RISK" && !data.alcoholIncreasingRiskInterventions?.length) {
      ctx.addIssue({ code: "custom", path: ["alcoholIncreasingRiskInterventions"], message: "Alcohol increasing risk interventions is required" });
    }

    const weightMeccBand = getWeightMeccBand(bmi);

    if (weightMeccBand === WEIGHT_MECC_BAND.HIGH && !data.weightHighRiskInterventions?.length) {
      ctx.addIssue({ code: "custom", path: ["weightHighRiskInterventions"], message: "Weight high risk interventions is required" });
    }

    if (weightMeccBand === WEIGHT_MECC_BAND.INCREASED && !data.weightIncreasedRiskInterventions?.length) {
      ctx.addIssue({ code: "custom", path: ["weightIncreasedRiskInterventions"], message: "Weight increased risk interventions is required" });
    }

    if (weightMeccBand === WEIGHT_MECC_BAND.NORMAL && !data.weightNormalInterventions?.length) {
      ctx.addIssue({ code: "custom", path: ["weightNormalInterventions"], message: "Weight normal interventions is required" });
    }

    const isLowActivity = (data.physicalActivityQ1 === "ZERO_DAYS" || data.physicalActivityQ1 === "ONE_TO_FOUR_DAYS") && data.physicalActivityQ2 === "NO_INADEQUATE";

    if (isLowActivity && !data.physicalActivityInterventions?.length) {
      ctx.addIssue({ code: "custom", path: ["physicalActivityInterventions"], message: "Physical activity interventions is required" });
    }

    // Ref 14: a checked additional risk factor must have a year of identification.
    additionalRiskFactorItems.forEach((item) => {
      if (data[item.field] && !data[`${item.field}Year`]) {
        ctx.addIssue({
          code: "custom",
          path: [`${item.field}Year`],
          message: "Year of identification is required",
        });
      }
    });
  });

export const RiskAndAssessmentForm = ({
  onBack,
  onContinue,
  data,
  isReview = false,
  isAnnualReview = false,
}) => {
  const [isAuditCModalOpen, setIsAuditCModalOpen] = useState(false);
  const [isQRisk3ModalOpen, setIsQRisk3ModalOpen] = useState(false);

  const { data: patient } = usePatientData();
  const { data: healthIdentifiers } = usePatientHealthIdentifiers();

  const currentYear = format(new Date(), "yyyy");

  // Additional risk factors follow the SAME visibility rules as the PP
  // registration reasons (OCF-referral reasons hidden as fresh picks,
  // Hypertension only for GMS/HAA/DVC, Gestational Diabetes / Pre-Eclampsia only
  // for female patients over 18) — a value already recorded on a saved review
  // always stays visible so it is never silently dropped.
  const visibleAdditionalRiskFactorItems = additionalRiskFactorItems.filter(
    (item) =>
      isPpReasonVisible({
        id: item.id,
        patient,
        haaNumber: healthIdentifiers?.haaNumber,
        alreadySelectedIds: data?.additionalRiskFactors ?? [],
      })
  );

  const additionalRiskFactorDefaults = additionalRiskFactorItems.reduce(
    (acc, item) => ({
      ...acc,
      [item.field]: (data?.additionalRiskFactors ?? []).includes(item.id),
      [`${item.field}Year`]:
        data?.yearOfAdditionalRiskFactor?.[item.id]?.toString() ?? currentYear,
    }),
    {}
  );

  // Only the BMI-matching weight band may be submitted (BE rejects multiple
  // X0139 categories) — render and seed only that band.
  const weightMeccBand = getWeightMeccBand(data?.bmi);

  const form = useForm({
    resolver: zodResolver(formSchema(data?.bmi)),
    defaultValues: {
      ...additionalRiskFactorDefaults,
      qRisk3Score: data?.qRisk3Score || "",
      tcHdlRatio: data?.tcHdlRatio || "",
      // QRisk3-only inputs with no other home on the PP form — persisted here so
      // the calculator choices survive a modal re-open. Cholesterol, BP and BMI
      // are NOT stored here: they are prefilled (read-only) from prior steps.
      qRisk3Sbps5: data?.qRisk3Sbps5 || "",
      // null (not 1) so the calculator can seed ethnicity from the patient's HSE
      // ethnic group on a new review; a stored review value still wins.
      qRisk3Ethrisk: data?.qRisk3Ethrisk ?? null,
      qRisk3Conditions: data?.qRisk3Conditions ?? {},
      qRisk3CigarettesPerDay: data?.qRisk3CigarettesPerDay || "",
      smokingStatus: data?.smokingStatus || "",
      vapingStatus: data?.vapingStatus || "",
      alcoholRiskScore: data?.alcoholRiskScore || "",
      alcoholFullAuditScore: data?.alcoholFullAuditScore || "",
      auditCQ1: data?.auditCQ1 ?? null,
      auditCQ2: data?.auditCQ2 ?? null,
      auditCQ3: data?.auditCQ3 ?? null,
      auditQ4: null,
      auditQ5: null,
      auditQ6: null,
      auditQ7: null,
      auditQ8: null,
      auditQ9: null,
      auditQ10: null,
      physicalActivityQ1: data?.physicalActivityQ1 || "",
      physicalActivityQ2: data?.physicalActivityQ2 || "",
      smokingInterventions: data?.smokingInterventions || [],
      alcoholDependenceInterventions:
        data?.alcoholDependenceInterventions || [],
      alcoholHigherRiskInterventions:
        data?.alcoholHigherRiskInterventions || [],
      alcoholIncreasingRiskInterventions:
        data?.alcoholIncreasingRiskInterventions || [],
      weightHighRiskInterventions:
        weightMeccBand === WEIGHT_MECC_BAND.HIGH
          ? data?.weightHighRiskInterventions || []
          : [],
      weightIncreasedRiskInterventions:
        weightMeccBand === WEIGHT_MECC_BAND.INCREASED
          ? data?.weightIncreasedRiskInterventions || []
          : [],
      weightNormalInterventions:
        weightMeccBand === WEIGHT_MECC_BAND.NORMAL
          ? data?.weightNormalInterventions || []
          : [],
      physicalActivityInterventions: data?.physicalActivityInterventions || [],
      medicationReview: data?.medicationReview || "",
      yearOfRegistration:
        data?.yearOfRegistration || format(new Date(), "yyyy"),
    },
  });

  const alcoholFullAuditScore = form.watch("alcoholFullAuditScore");
  const alcoholInterventionCategory = getAlcoholInterventionCategory(alcoholFullAuditScore);

  useEffect(() => {
    if (alcoholInterventionCategory !== "DEPENDENCE") form.setValue("alcoholDependenceInterventions", []);
    if (alcoholInterventionCategory !== "HIGHER_RISK") form.setValue("alcoholHigherRiskInterventions", []);
    if (alcoholInterventionCategory !== "INCREASING_RISK") form.setValue("alcoholIncreasingRiskInterventions", []);
  }, [alcoholInterventionCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const physicalActivityQ1 = form.watch("physicalActivityQ1");
  const physicalActivityQ2 = form.watch("physicalActivityQ2");
  const isLowActivity = (physicalActivityQ1 === "ZERO_DAYS" || physicalActivityQ1 === "ONE_TO_FOUR_DAYS") && physicalActivityQ2 === "NO_INADEQUATE";

  useEffect(() => {
    if (!isLowActivity) form.setValue("physicalActivityInterventions", []);
  }, [isLowActivity]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFormSubmit = (values) => {
    const checkedAdditionalRiskFactors = additionalRiskFactorItems.filter(
      (item) => values[item.field]
    );
    const additionalRiskFactors = checkedAdditionalRiskFactors.map(
      (item) => item.id
    );
    const yearOfAdditionalRiskFactor = checkedAdditionalRiskFactors.reduce(
      (acc, item) => ({
        ...acc,
        [item.id]: Number(values[`${item.field}Year`]),
      }),
      {}
    );

    onContinue({ ...values, additionalRiskFactors, yearOfAdditionalRiskFactor });
  };

  return (
    <ItemFormWrapper
      onBack={onBack}
      onContinue={form.handleSubmit(handleFormSubmit, onInvalidSubmit("RiskAndAssessmentForm"))}
      isReview={isReview}
    >
      <div
        className={style.itemWrapper}
        style={{
          marginBottom: "0px",
          paddingBottom: "0px",
          borderBottom: "none",
        }}
      >
        <FormProvider {...form}>
          <form
            className={style.addReferralForm}
            onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("RiskAndAssessmentForm"))}
          >
            <div className={style.itemWrapper}>
              <h3>Disease Assessment Scoring</h3>
              <div
                className={style.inputGroupGrid3Columns}
                style={{ marginBottom: "24px", marginTop: "16px" }}
              >
                <FormField name="medicationReview">
                  <label className={style.addReferralFormLabel}>
                    Medication review completed?
                  </label>
                  <Select
                    options={YES_NO_OPTIONS.map((o) => ({ ...o, value: o.value.toLowerCase() }))}
                    placeholder="Select"
                    disabled={isReview}
                    value={form.watch("medicationReview")}
                    onChange={(value) =>
                      form.setValue("medicationReview", value, {
                        shouldValidate: true,
                      })
                    }
                    required
                  />
                </FormField>
                <FormField name="yearOfRegistration">
                  <label className={style.addReferralFormLabel}>
                    Year of Registration
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="2025"
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9.]/g, ""))
                    }
                    className="form-control"
                    disabled={isReview}
                    {...form.register("yearOfRegistration")}
                  />
                </FormField>
                <FormField name="qRisk3Score">
                  <label className={style.addReferralFormLabel}>
                    QRisk 3 Score
                  </label>
                  <input type="hidden" {...form.register("qRisk3Score")} />
                  <CalculateInputTrigger
                    value={form.watch("qRisk3Score")}
                    onClick={() => setIsQRisk3ModalOpen(true)}
                    disabled={isReview}
                  />
                </FormField>
              </div>
              <h3 className={style.headingWithInfo}>
                Risk Factors
                <InfoTooltip text={MECC_INTERVENTION_INFO} />
              </h3>

              <div className={style.inputGroupGrid3Columns} style={{ marginTop: "12px" }}>
                <FormField name="smokingStatus">
                  <label className={style.addReferralFormLabel}>Smoking Status</label>
                  <Select
                    options={SMOKING_STATUS_OPTIONS}
                    placeholder="Select"
                    disabled={isReview}
                    value={form.watch("smokingStatus")}
                    onChange={(value) => form.setValue("smokingStatus", value, { shouldValidate: true })}
                    required
                  />
                </FormField>
                <FormField name="vapingStatus">
                  <label className={style.addReferralFormLabel}>Vaping Status</label>
                  <Select
                    options={VAPING_STATUS_OPTIONS}
                    placeholder="Select"
                    disabled={isReview}
                    value={form.watch("vapingStatus")}
                    onChange={(value) => form.setValue("vapingStatus", value, { shouldValidate: true })}
                    required
                  />
                </FormField>
                <FormField name="smokingInterventions">
                  <label className={style.addReferralFormLabel}>Smoking Interventions</label>
                  <MultiSelect
                    options={SMOKING_INTERVENTIONS_OPTIONS}
                    placeholder="Select"
                    disabled={isReview}
                    value={form.watch("smokingInterventions")}
                    onChange={(value) => form.setValue("smokingInterventions", value, { shouldValidate: true })}
                  />
                  <InterventionInfoHints
                    options={SMOKING_INTERVENTIONS_OPTIONS}
                    selectedValues={form.watch("smokingInterventions")}
                    className={style.qRiskHint}
                  />
                </FormField>
                <FormField name="alcoholRiskScore">
                  <label className={style.addReferralFormLabel}>Alcohol Risk Score</label>
                  <input type="hidden" {...form.register("alcoholRiskScore")} />
                  <CalculateInputTrigger
                    value={form.watch("alcoholRiskScore")}
                    onClick={() => setIsAuditCModalOpen(true)}
                    disabled={isReview}
                  />
                </FormField>
                {alcoholInterventionCategory === "DEPENDENCE" && (
                  <FormField name="alcoholDependenceInterventions">
                    <label className={style.addReferralFormLabel}>Alcohol Dependence Interventions</label>
                    <MultiSelect
                      options={ALCOHOL_DEPENDENCE_INTERVENTIONS_OPTIONS}
                      placeholder="Select"
                      disabled={isReview}
                      value={form.watch("alcoholDependenceInterventions")}
                      onChange={(value) => form.setValue("alcoholDependenceInterventions", value, { shouldValidate: true })}
                    />
                    <InterventionInfoHints
                      options={ALCOHOL_DEPENDENCE_INTERVENTIONS_OPTIONS}
                      selectedValues={form.watch("alcoholDependenceInterventions")}
                      className={style.qRiskHint}
                    />
                  </FormField>
                )}
                {alcoholInterventionCategory === "HIGHER_RISK" && (
                  <FormField name="alcoholHigherRiskInterventions">
                    <label className={style.addReferralFormLabel}>Alcohol Higher Risk Interventions</label>
                    <MultiSelect
                      options={ALCOHOL_HIGHER_RISK_INTERVENTIONS_OPTIONS}
                      placeholder="Select"
                      disabled={isReview}
                      value={form.watch("alcoholHigherRiskInterventions")}
                      onChange={(value) => form.setValue("alcoholHigherRiskInterventions", value, { shouldValidate: true })}
                    />
                    <InterventionInfoHints
                      options={ALCOHOL_HIGHER_RISK_INTERVENTIONS_OPTIONS}
                      selectedValues={form.watch("alcoholHigherRiskInterventions")}
                      className={style.qRiskHint}
                    />
                  </FormField>
                )}
                {alcoholInterventionCategory === "INCREASING_RISK" && (
                  <FormField name="alcoholIncreasingRiskInterventions">
                    <label className={style.addReferralFormLabel}>Alcohol Increasing Risk Interventions</label>
                    <MultiSelect
                      options={ALCOHOL_INCREASING_RISK_INTERVENTIONS_OPTIONS}
                      placeholder="Select"
                      disabled={isReview}
                      value={form.watch("alcoholIncreasingRiskInterventions")}
                      onChange={(value) => form.setValue("alcoholIncreasingRiskInterventions", value, { shouldValidate: true })}
                    />
                    <InterventionInfoHints
                      options={ALCOHOL_INCREASING_RISK_INTERVENTIONS_OPTIONS}
                      selectedValues={form.watch("alcoholIncreasingRiskInterventions")}
                      className={style.qRiskHint}
                    />
                  </FormField>
                )}
                <FormField name="physicalActivityQ1">
                  <label className={style.addReferralFormLabel}>
                    Physical activity – active days
                  </label>
                  <Select
                    options={PHYSICAL_ACTIVITY_Q1_OPTIONS}
                    placeholder="Select"
                    disabled={isReview}
                    value={form.watch("physicalActivityQ1")}
                    onChange={(value) => form.setValue("physicalActivityQ1", value, { shouldValidate: true })}
                    required
                  />
                  <p className={style.qRiskHint}>
                    In a typical week, how many days have you been physically
                    active (PA) for total of 30 minutes or more?
                  </p>
                </FormField>
                <FormField name="physicalActivityQ2">
                  <label className={style.addReferralFormLabel}>
                    Physical activity – weekly target
                  </label>
                  <Select
                    options={PHYSICAL_ACTIVITY_Q2_OPTIONS}
                    placeholder="Select"
                    disabled={isReview}
                    value={form.watch("physicalActivityQ2")}
                    onChange={(value) => form.setValue("physicalActivityQ2", value, { shouldValidate: true })}
                    required
                  />
                  <p className={style.qRiskHint}>
                    If four days or less, in a typical week have you been PA for
                    either 150 minutes moderate or 75 minutes vigorous activity?
                  </p>
                </FormField>
                {!!isLowActivity && (
                  <FormField name="physicalActivityInterventions">
                    <label className={style.addReferralFormLabel}>Physical Activity Interventions</label>
                    <MultiSelect
                      options={PHYSICAL_ACTIVITY_INTERVENTIONS_OPTIONS}
                      placeholder="Select"
                      disabled={isReview}
                      value={form.watch("physicalActivityInterventions")}
                      onChange={(value) => form.setValue("physicalActivityInterventions", value, { shouldValidate: true })}
                    />
                    <InterventionInfoHints
                      options={PHYSICAL_ACTIVITY_INTERVENTIONS_OPTIONS}
                      selectedValues={form.watch("physicalActivityInterventions")}
                      className={style.qRiskHint}
                    />
                  </FormField>
                )}
                {weightMeccBand === WEIGHT_MECC_BAND.HIGH && (
                  <FormField name="weightHighRiskInterventions">
                    <label className={style.addReferralFormLabel}>Weight High Risk Interventions</label>
                    <MultiSelect
                      options={WEIGHT_HIGH_RISK_INTERVENTIONS_OPTIONS}
                      placeholder="Select"
                      disabled={isReview}
                      value={form.watch("weightHighRiskInterventions")}
                      onChange={(value) => form.setValue("weightHighRiskInterventions", value, { shouldValidate: true })}
                    />
                    <InterventionInfoHints
                      options={WEIGHT_HIGH_RISK_INTERVENTIONS_OPTIONS}
                      selectedValues={form.watch("weightHighRiskInterventions")}
                      className={style.qRiskHint}
                    />
                  </FormField>
                )}
                {weightMeccBand === WEIGHT_MECC_BAND.INCREASED && (
                  <FormField name="weightIncreasedRiskInterventions">
                    <label className={style.addReferralFormLabel}>Weight Increased Risk Interventions</label>
                    <MultiSelect
                      options={WEIGHT_INCREASED_RISK_INTERVENTIONS_OPTIONS}
                      placeholder="Select"
                      disabled={isReview}
                      value={form.watch("weightIncreasedRiskInterventions")}
                      onChange={(value) => form.setValue("weightIncreasedRiskInterventions", value, { shouldValidate: true })}
                    />
                    <InterventionInfoHints
                      options={WEIGHT_INCREASED_RISK_INTERVENTIONS_OPTIONS}
                      selectedValues={form.watch("weightIncreasedRiskInterventions")}
                      className={style.qRiskHint}
                    />
                  </FormField>
                )}
                {weightMeccBand === WEIGHT_MECC_BAND.NORMAL && (
                  <FormField name="weightNormalInterventions">
                    <label className={style.addReferralFormLabel}>Weight Normal Interventions</label>
                    <MultiSelect
                      options={WEIGHT_NORMAL_INTERVENTIONS_OPTIONS}
                      placeholder="Select"
                      disabled={isReview}
                      value={form.watch("weightNormalInterventions")}
                      onChange={(value) => form.setValue("weightNormalInterventions", value, { shouldValidate: true })}
                    />
                    <InterventionInfoHints
                      options={WEIGHT_NORMAL_INTERVENTIONS_OPTIONS}
                      selectedValues={form.watch("weightNormalInterventions")}
                      className={style.qRiskHint}
                    />
                  </FormField>
                )}
              </div>
            </div>
            {!!isAnnualReview && (
              <div className={style.itemWrapper}>
                <h3>Additional Risk Factor since registration</h3>
                <div
                  className={dialogStyle.tableContainer}
                  style={{ marginTop: "12px" }}
                >
                  <table className={dialogStyle.table}>
                    <thead className={dialogStyle.thead}>
                      <tr>
                        <th className={dialogStyle.th}></th>
                        <th className={dialogStyle.th}>Risk Factor</th>
                        <th className={dialogStyle.th}>Year of identification</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleAdditionalRiskFactorItems.map((item) => (
                        <tr key={item.id}>
                          <td className={dialogStyle.tdName}>
                            <CustomCheckbox
                              checked={form.watch(item.field)}
                              disabled={isReview}
                              onChange={(event) =>
                                form.setValue(item.field, event.target.checked, {
                                  shouldValidate: true,
                                })
                              }
                            />
                          </td>
                          <td title="Risk Factor" className={dialogStyle.tdName}>
                            {item.name}
                          </td>
                          <td title="Year" className={dialogStyle.tdName}>
                            <Select
                              small
                              options={Array.from(
                                { length: 150 },
                                (_, index) => getYear(new Date()) - index
                              ).map((year) => ({ value: year, label: year }))}
                              disabled={isReview || !form.watch(item.field)}
                              value={form.watch(`${item.field}Year`) ?? ""}
                              onChange={(value) =>
                                form.setValue(`${item.field}Year`, value, {
                                  shouldValidate: true,
                                })
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </form>
        </FormProvider>
      </div>
      <AuditCCalculatorModal
        isOpen={isAuditCModalOpen}
        onClose={() => setIsAuditCModalOpen(false)}
        onConfirm={(score, q1, q2, q3, fullAuditScore, q4, q5, q6, q7, q8, q9, q10) => {
          form.setValue("alcoholRiskScore", score, { shouldValidate: true });
          form.setValue("alcoholFullAuditScore", fullAuditScore ?? "");
          form.setValue("auditCQ1", q1);
          form.setValue("auditCQ2", q2);
          form.setValue("auditCQ3", q3);
          form.setValue("auditQ4", q4);
          form.setValue("auditQ5", q5);
          form.setValue("auditQ6", q6);
          form.setValue("auditQ7", q7);
          form.setValue("auditQ8", q8);
          form.setValue("auditQ9", q9);
          form.setValue("auditQ10", q10);
        }}
        prefillQ1={form.watch("auditCQ1")}
        prefillQ2={form.watch("auditCQ2")}
        prefillQ3={form.watch("auditCQ3")}
        prefillQ4={form.watch("auditQ4")}
        prefillQ5={form.watch("auditQ5")}
        prefillQ6={form.watch("auditQ6")}
        prefillQ7={form.watch("auditQ7")}
        prefillQ8={form.watch("auditQ8")}
        prefillQ9={form.watch("auditQ9")}
        prefillQ10={form.watch("auditQ10")}
      />
      <QRisk3CalculatorModal
        isOpen={isQRisk3ModalOpen}
        onClose={() => setIsQRisk3ModalOpen(false)}
        onConfirm={(score, ratio, ethrisk, conditions, cigarettesPerDay) => {
          form.setValue("qRisk3Score", score, { shouldValidate: true });
          form.setValue("tcHdlRatio", ratio);
          form.setValue("qRisk3Ethrisk", ethrisk);
          form.setValue("qRisk3Conditions", conditions);
          form.setValue("qRisk3CigarettesPerDay", cigarettesPerDay);
        }}
        // Only the QRisk3-only inputs round-trip here. Cholesterol, BP and BMI are
        // prefill-only — their source fields live on the now-unmounted Tests and
        // Review & Exam steps, so we deliberately do NOT write those back.
        onFieldChange={({ sbps5, ethrisk, cigarettesPerDay }) => {
          form.setValue("qRisk3Sbps5", sbps5 ?? "");
          form.setValue("qRisk3Ethrisk", ethrisk);
          form.setValue("qRisk3CigarettesPerDay", cigarettesPerDay ?? "");
        }}
        // Prefill-only from prior steps: bloods (Tests & Investigations step) +
        // obs (Review & Exam step) + smoking status (this step); patient age/sex
        // are read inside the modal.
        prefillBmi={data?.bmi}
        prefillSbp={data?.bp1}
        prefillSmokingStatus={form.watch("smokingStatus")}
        prefillTotalCholesterol={data?.totalCholesterol}
        prefillHdl={data?.hdlCholesterol}
        prefillSbps5={form.watch("qRisk3Sbps5")}
        prefillEthrisk={form.watch("qRisk3Ethrisk")}
        prefillConditions={form.watch("qRisk3Conditions")}
        prefillCigarettesPerDay={form.watch("qRisk3CigarettesPerDay")}
        // Seed the calculator from the ethnicity chosen on this review's initial
        // step (falls back to the patient record inside the modal).
        selectedEthnicityCode={data?.ethnicityCode}
      />
    </ItemFormWrapper>
  );
};
